import React, { useEffect, useRef, useState } from 'react';
import SystemHealthService from '../../../services/SystemHealthService';
import { PageHeader, Card, StatTile, Badge, EmptyState } from '../../ui';

const POLL_MS = 4000;

const fmtMB = (mb) => {
  if (mb == null) return '—';
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
};
const fmtBps = (bytesPerSec) => {
  if (!bytesPerSec) return '0 Kbps';
  const kbps = (bytesPerSec * 8) / 1000;
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(2)} Mbps`;
  return `${Math.round(kbps)} Kbps`;
};
const fmtUptime = (sec) => {
  if (!sec) return '—';
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// green under 60%, amber 60–85%, red above 85% — matches the thresholds used
// to spot the nginx-CPU-saturation incidents on this box (load ~9 on 8 cores).
const toneForPct = (pct) => (pct >= 85 ? 'live' : pct >= 60 ? 'warn' : 'good');
const barColor = (pct) => (pct >= 85 ? 'tw-bg-live' : pct >= 60 ? 'tw-bg-warn' : 'tw-bg-good');

const Bar = ({ pct, label, sub }) => (
  <div>
    <div className="tw-mb-1.5 tw-flex tw-items-center tw-justify-between tw-text-xs">
      <span className="tw-font-medium tw-text-content-muted">{label}</span>
      <span className="tw-font-semibold tw-text-heading">{Math.round(pct)}%</span>
    </div>
    <div className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-ink-800">
      <div
        className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 ${barColor(pct)}`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
    {sub && <div className="tw-mt-1 tw-text-[0.7rem] tw-text-content-faint">{sub}</div>}
  </div>
);

const SystemHealth = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await SystemHealthService.getSnapshot();
        if (!cancelled && res?.success) { setData(res.data); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    poll();
    if (!paused) timerRef.current = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(timerRef.current); };
  }, [paused]);

  const load1m = data?.load?.['1m'] ?? 0;
  const cpuCount = data?.cpu?.count || 1;
  const loadPct = Math.min(100, (load1m / cpuCount) * 100);
  const overallTone = toneForPct(loadPct);

  return (
    <div className="tw-p-4 md:tw-p-6">
      <PageHeader
        title="Server Health"
        subtitle="Real-time CPU load, memory, disk and process usage for app.teve.ca"
        actions={
          <div className="tw-flex tw-items-center tw-gap-2">
            <Badge tone={data ? overallTone : 'neutral'}>
              <span className={`tw-mr-1.5 tw-inline-block tw-h-1.5 tw-w-1.5 tw-rounded-full ${paused ? 'tw-bg-content-faint' : 'tw-animate-pulse tw-bg-current'}`} />
              {paused ? 'Paused' : 'Live'}
            </Badge>
            <button
              onClick={() => setPaused((p) => !p)}
              className="tw-rounded-lg tw-bg-ink-800 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-content-muted hover:tw-text-heading"
            >
              <i className={`fa-solid ${paused ? 'fa-play' : 'fa-pause'} tw-mr-1.5`} />
              {paused ? 'Resume' : 'Pause'}
            </button>
          </div>
        }
      />

      {error && !data && (
        <Card><EmptyState icon="fa-triangle-exclamation" title="Couldn't load server health" hint={error} /></Card>
      )}

      {!data && !error && loading && (
        <Card><EmptyState icon="fa-gauge-high" title="Loading live metrics…" /></Card>
      )}

      {data && (
        <>
          <div className="tw-mb-5 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
            <StatTile
              icon="fa-microchip" tone={overallTone}
              label="1-min load average"
              value={load1m.toFixed(2)}
              hint={`${cpuCount} core${cpuCount === 1 ? '' : 's'} · ${Math.round(loadPct)}% of capacity`}
            />
            <StatTile
              icon="fa-chart-simple" tone={toneForPct(data.cpu?.overall || 0)}
              label="CPU usage (instant)"
              value={`${Math.round(data.cpu?.overall || 0)}%`}
              hint="Sampled every 4s"
            />
            <StatTile
              icon="fa-memory" tone={toneForPct(data.memory?.used_pct || 0)}
              label="Memory used"
              value={fmtMB(data.memory?.used_mb)}
              hint={`of ${fmtMB(data.memory?.total_mb)} (${Math.round(data.memory?.used_pct || 0)}%)`}
            />
            <StatTile
              icon="fa-clock" tone="brand"
              label="Server uptime"
              value={fmtUptime(data.uptime_sec)}
              hint={new Date(data.at).toLocaleTimeString()}
            />
          </div>

          <div className="tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-2">
            <Card title="CPU load" subtitle="Load average vs. core count, and per-core instant usage">
              <div className="tw-space-y-4">
                <Bar pct={loadPct} label={`Load average (1m / 5m / 15m: ${data.load?.['1m']?.toFixed(2)} / ${data.load?.['5m']?.toFixed(2)} / ${data.load?.['15m']?.toFixed(2)})`} />
                {(data.cpu?.cores || []).length > 0 && (
                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
                    {data.cpu.cores.map((pct, i) => (
                      <Bar key={i} pct={pct} label={`Core ${i + 1}`} />
                    ))}
                  </div>
                )}
                {loadPct >= 85 && (
                  <div className="tw-rounded-lg tw-bg-live/10 tw-px-3 tw-py-2 tw-text-xs tw-text-live">
                    <i className="fa-solid fa-triangle-exclamation tw-mr-1.5" />
                    Load is at or above capacity — expect HLS/RTMP stalls (viewer buffering) until this drops.
                  </div>
                )}
              </div>
            </Card>

            <Card title="Memory & disk" subtitle="RAM, swap, and storage on the media server">
              <div className="tw-space-y-4">
                <Bar pct={data.memory?.used_pct || 0} label="RAM" sub={`${fmtMB(data.memory?.used_mb)} / ${fmtMB(data.memory?.total_mb)}`} />
                {data.swap && (
                  <Bar pct={data.swap.used_pct || 0} label="Swap" sub={`${fmtMB(data.swap.used_mb)} / ${fmtMB(data.swap.total_mb)}`} />
                )}
                {(data.disks || []).map((d) => (
                  <Bar key={d.mount} pct={d.used_pct} label={`Disk — ${d.mount}`} sub={`${fmtMB(d.used_mb)} / ${fmtMB(d.total_mb)}`} />
                ))}
              </div>
            </Card>
          </div>

          <div className="tw-mt-5 tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-2">
            <Card title="Top processes" subtitle="Grouped by name — nginx handles RTMP relay + HLS, ffmpeg handles failover/switcher mixing">
              {data.top_processes?.length ? (
                <div className="tw-overflow-hidden tw-rounded-xl">
                  <table className="tw-w-full tw-text-sm">
                    <thead>
                      <tr className="tw-text-left tw-text-xs tw-text-content-faint">
                        <th className="tw-pb-2 tw-font-medium">Process</th>
                        <th className="tw-pb-2 tw-font-medium">Instances</th>
                        <th className="tw-pb-2 tw-font-medium">CPU</th>
                        <th className="tw-pb-2 tw-font-medium">Memory</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_processes.map((p) => (
                        <tr key={p.name} className="tw-border-t tw-border-ink-800">
                          <td className="tw-py-2 tw-font-medium tw-text-heading">{p.name}</td>
                          <td className="tw-py-2 tw-text-content-muted">{p.count}</td>
                          <td className="tw-py-2">
                            <Badge tone={toneForPct(p.cpu)}>{p.cpu}%</Badge>
                          </td>
                          <td className="tw-py-2 tw-text-content-muted">{p.mem}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="fa-list" title="No process data" hint="Not available on this platform." />
              )}
            </Card>

            <Card title="RTMP / streaming" subtitle="Live from nginx-rtmp — for correlating load with actual stream traffic">
              {data.rtmp ? (
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <StatTile icon="fa-video" tone="brand" label="Active streams" value={data.rtmp.active_streams} />
                  <StatTile icon="fa-users" tone="info" label="Connected clients" value={data.rtmp.total_clients} />
                  <StatTile icon="fa-tv" tone="info" label="HLS viewers" value={data.rtmp.total_hls_viewers} />
                  <StatTile icon="fa-arrow-down" tone="good" label="Bandwidth in" value={fmtBps(data.rtmp.bandwidth_in)} />
                </div>
              ) : (
                <EmptyState icon="fa-satellite-dish" title="RTMP stats unavailable" hint="nginx stat endpoint didn't respond." />
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default SystemHealth;
